<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';
import getUnixTime from 'date-fns/getUnixTime';

import { useAlert } from 'dashboard/composables';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import DataCollectionSummaryCards from './dataCollection/components/DataCollectionSummaryCards.vue';
import DataCollectionTrendChart from './dataCollection/components/DataCollectionTrendChart.vue';
import DataCollectionFieldCoverage from './dataCollection/components/DataCollectionFieldCoverage.vue';
import DataCollectionCustomersTable from './dataCollection/components/DataCollectionCustomersTable.vue';

const store = useStore();
const { t } = useI18n();

const today = new Date();
const dateRange = ref([subDays(today, 29), today]);
const statusFilter = ref('all');
const searchTerm = ref('');
const selectedFields = ref([]);
const sortBy = ref('last_collected_at');
const sortDirection = ref('desc');
const pageIndex = ref(1);
const perPage = ref(20);

const defaultUiFlags = {
  isFetchingSummary: false,
  isFetchingCustomers: false,
  isFetchingFields: false,
  isExporting: false,
};
const summary = computed(
  () =>
    store.getters.getDataCollectionSummary || {
      totals: {},
      trend: [],
      fieldCoverage: [],
    }
);
const customers = computed(
  () => store.getters.getDataCollectionCustomers || []
);
const meta = computed(() => store.getters.getDataCollectionCustomersMeta || {});
const fields = computed(() => store.getters.getDataCollectionFields || []);
const uiFlags = computed(
  () => store.getters.getDataCollectionReportUIFlags || defaultUiFlags
);

const statusOptions = [
  { id: 'all', label: t('DATA_COLLECTION_REPORTS.STATUS.ALL') },
  { id: 'completed', label: t('DATA_COLLECTION_REPORTS.STATUS.COMPLETED') },
  { id: 'in_progress', label: t('DATA_COLLECTION_REPORTS.STATUS.IN_PROGRESS') },
  { id: 'pending', label: t('DATA_COLLECTION_REPORTS.STATUS.PENDING') },
];

const sortOptions = [
  { id: 'completion', label: t('DATA_COLLECTION_REPORTS.SORT.BY_COMPLETION') },
  {
    id: 'last_collected_at',
    label: t('DATA_COLLECTION_REPORTS.SORT.BY_LAST_COLLECTED'),
  },
  { id: 'name', label: t('DATA_COLLECTION_REPORTS.SORT.BY_NAME') },
];

const directionOptions = [
  { id: 'desc', label: t('DATA_COLLECTION_REPORTS.SORT.DESC') },
  { id: 'asc', label: t('DATA_COLLECTION_REPORTS.SORT.ASC') },
];

const normalizedStatus = computed(() => {
  if (statusFilter.value === 'all') {
    return undefined;
  }
  return statusFilter.value;
});

const baseParams = computed(() => ({
  since: getUnixTime(startOfDay(dateRange.value[0])),
  until: getUnixTime(endOfDay(dateRange.value[1])),
  fieldIds: selectedFields.value.map(field => field.id),
}));

const customersParams = computed(() => ({
  ...baseParams.value,
  status: normalizedStatus.value,
  search: searchTerm.value || undefined,
  sortBy: sortBy.value,
  sortDirection: sortDirection.value,
  page: pageIndex.value,
  perPage: perPage.value,
}));

const normalizeServerError = async error => {
  const responseData = error?.response?.data;
  if (typeof responseData === 'string') {
    return responseData;
  }

  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      try {
        const parsed = JSON.parse(text);
        return parsed?.error || text;
      } catch {
        return text;
      }
    } catch {
      return null;
    }
  }

  return responseData?.error || null;
};

const lastToast = ref({ key: null, at: 0 });
const showErrorToast = async error => {
  const serverMessage = await normalizeServerError(error);
  const key = serverMessage || 'unknown';
  const now = Date.now();
  if (lastToast.value.key === key && now - lastToast.value.at < 3000) {
    return;
  }
  lastToast.value = { key, at: now };

  if (serverMessage === 'Data collection configuration is not ready') {
    useAlert(t('DATA_COLLECTION_REPORTS.ERRORS.CONFIG_NOT_READY'), {
      type: 'error',
    });
    return;
  }

  useAlert(t('DATA_COLLECTION_REPORTS.ERRORS.LOAD_FAILED'), { type: 'error' });
};

const loadSummary = async () => {
  try {
    await store.dispatch('fetchDataCollectionSummary', baseParams.value);
  } catch (error) {
    await showErrorToast(error);
  }
};

const loadCustomers = async () => {
  try {
    await store.dispatch('fetchDataCollectionCustomers', customersParams.value);
  } catch (error) {
    await showErrorToast(error);
  }
};

const handleDownload = async () => {
  try {
    await store.dispatch('downloadDataCollectionCustomers', {
      ...customersParams.value,
      fileName: `data-collection-customers-${new Date().toISOString().slice(0, 10)}.xlsx`,
    });
  } catch (error) {
    await showErrorToast(error);
  }
};

watch(
  baseParams,
  () => {
    const previousPage = pageIndex.value;
    void loadSummary();
    if (previousPage === 1) {
      void loadCustomers();
    } else {
      pageIndex.value = 1;
    }
  },
  { deep: true }
);

watch(
  [statusFilter, sortBy, sortDirection],
  () => {
    if (pageIndex.value === 1) {
      void loadCustomers();
    } else {
      pageIndex.value = 1;
    }
  },
  { deep: true }
);

watch(
  () => pageIndex.value,
  () => {
    void loadCustomers();
  }
);

let searchDebounce = null;
watch(
  () => searchTerm.value,
  () => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
      if (pageIndex.value === 1) {
        void loadCustomers();
      } else {
        pageIndex.value = 1;
      }
    }, 400);
  }
);

onBeforeUnmount(() => {
  clearTimeout(searchDebounce);
});

onMounted(() => {
  store.dispatch('fetchDataCollectionFields').catch(showErrorToast);
  void loadSummary();
  void loadCustomers();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      class="rounded-2xl border border-n-weak bg-white/90 p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/30"
    >
      <div
        class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
      >
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12 dark:text-white">
            {{ $t('DATA_COLLECTION_REPORTS.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-gray-6 dark:text-n-gray-3">
            {{ $t('DATA_COLLECTION_REPORTS.DESCRIPTION') }}
          </p>
        </div>
        <woot-button
          variant="smooth"
          color-scheme="primary"
          :is-loading="uiFlags.isExporting"
          @click="handleDownload"
        >
          {{ $t('DATA_COLLECTION_REPORTS.ACTIONS.DOWNLOAD') }}
        </woot-button>
      </div>

      <div class="mt-6 flex flex-col gap-4">
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-n-gray-5 dark:text-n-gray-3"
            >
              {{ $t('DATA_COLLECTION_REPORTS.FILTERS.DATE_RANGE') }}
            </label>
            <WootDateRangePicker
              v-model="dateRange"
              class="mt-2"
              :show-shortcuts="true"
              :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
              :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            />
          </div>
          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-n-gray-5 dark:text-n-gray-3"
            >
              {{ $t('DATA_COLLECTION_REPORTS.FILTERS.FIELDS') }}
            </label>
            <multiselect
              v-model="selectedFields"
              class="mt-2"
              label="displayName"
              track-by="id"
              :options="fields"
              :multiple="true"
              :close-on-select="false"
              :show-labels="false"
              :placeholder="
                $t('DATA_COLLECTION_REPORTS.FILTERS.FIELD_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label
              class="block text-xs font-semibold uppercase tracking-wide text-n-gray-5 dark:text-n-gray-3"
            >
              {{ $t('DATA_COLLECTION_REPORTS.FILTERS.SEARCH') }}
            </label>
            <input
              v-model="searchTerm"
              type="text"
              :placeholder="
                $t('DATA_COLLECTION_REPORTS.FILTERS.SEARCH_PLACEHOLDER')
              "
              class="mt-2 w-full rounded-lg border border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-gray-4 focus:border-primary-500 focus:outline-none dark:border-white/10 dark:bg-slate-900 dark:text-white"
            />
          </div>
        </div>

        <div
          class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-for="option in statusOptions"
              :key="option.id"
              type="button"
              class="rounded-full border px-3 py-1 text-sm font-semibold transition"
              :class="[
                statusFilter === option.id
                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
                  : 'border-n-gray-2 text-n-gray-6 hover:border-primary-200 dark:border-n-gray-7 dark:text-n-gray-3',
              ]"
              @click="statusFilter = option.id"
            >
              {{ option.label }}
            </button>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <div class="flex items-center gap-2">
              <label
                class="text-xs font-semibold uppercase tracking-wide text-n-gray-5 dark:text-n-gray-3"
              >
                {{ $t('DATA_COLLECTION_REPORTS.SORT.LABEL') }}
              </label>
              <select
                v-model="sortBy"
                class="rounded-lg border border-n-weak bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-slate-900 dark:text-white"
              >
                <option
                  v-for="option in sortOptions"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.label }}
                </option>
              </select>
              <select
                v-model="sortDirection"
                class="rounded-lg border border-n-weak bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-slate-900 dark:text-white"
              >
                <option
                  v-for="option in directionOptions"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DataCollectionSummaryCards
      :totals="summary.totals"
      :is-loading="uiFlags.isFetchingSummary"
    />

    <div class="grid gap-4 xl:grid-cols-3">
      <div class="xl:col-span-2">
        <DataCollectionTrendChart
          :data="summary.trend"
          :is-loading="uiFlags.isFetchingSummary"
        />
      </div>
      <DataCollectionFieldCoverage
        :fields="summary.fieldCoverage"
        :total-customers="summary.totals.totalCustomers"
      />
    </div>

    <DataCollectionCustomersTable
      :customers="customers"
      :meta="meta"
      :is-loading="uiFlags.isFetchingCustomers"
      :page="pageIndex"
      :page-size="perPage"
      :field-definitions="fields"
      @page-change="pageIndex = $event"
    />
  </div>
</template>

<style scoped>
:global(.dark) .multiselect__input,
:global(.dark) .multiselect__single {
  color: white;
}
</style>
